import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import { Theme } from "../../data/style/Theme";
import StatsBanner from "../Display/StatsBanner";
import { useAuth } from "../../context/AuthContext";
import { useMutation } from "@tanstack/react-query";
import { errorTexts } from "../../data/assets/texts";
import { getExerciseProgress } from "../../api/user";
import { useNotifications } from "../../context/NotificationContext";
import { MainText, SecondaryTitle, SpacedRow } from "../../data/style/Style";
import {
  LineChart,
  Line,
  YAxis,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const CustomTooltip = ({ active, payload }) => {
  const { settings } = useAuth();
  if (active && payload && payload.length) {
    let data = payload[0].payload;

    return (
      <Stack
        sx={{
          background: Theme.palette.card,
          border: 1,
          borderColor: Theme.palette.orange,
          width: "250px",
          padding: 1,
          borderRadius: 3,
        }}
        gap={1}
      >
        <MainText fontSize={"0.8rem"}>
          <span style={{ color: Theme.palette.orange }}>Date:</span>
          &nbsp;
          {data.date}
        </MainText>
        <MainText fontSize={"0.8rem"}>
          {data.exercise_type === 3 ? (
            <>
              <span style={{ color: Theme.palette.orange }}>Time:</span>
              &nbsp;
              {data.converted_score}&nbsp;minutes
            </>
          ) : data.exercise_type === 2 ? (
            <>
              <span style={{ color: Theme.palette.orange }}>Reps:</span>
              &nbsp;
              {data.value}
            </>
          ) : (
            <>
              <span style={{ color: Theme.palette.orange }}>Weight:</span>
              &nbsp;
              {settings.weight === 0 ? (
                <>{data.value}&nbsp;kg</>
              ) : (
                <>{data.weight_lb}&nbsp;lb</>
              )}
            </>
          )}
        </MainText>
        {data.video && (
          <video
            style={{ alignSelf: "center" }}
            src={data.video}
            width={200}
            loop
            autoPlay
            muted
          ></video>
        )}
      </Stack>
    );
  }

  return null;
};

const ExerciseChart = ({ selectedExercise, exerciseType, setSessions }) => {
  const { settings } = useAuth();
  const [chartData, setChartData] = useState([]);
  const { enableNotification } = useNotifications();
  const [impPct, setImpPCt] = useState(0);

  const { mutate } = useMutation({
    mutationFn: getExerciseProgress,
    onSuccess: (response) => {
      setChartData(response.data.progress);
      setSessions(response.data.progress.length);
    },
    onError: (error) => {
      if (error.response.status === 400)
        enableNotification(error.response.data.message, "error");

      if (error.response.status >= 500)
        enableNotification(errorTexts.system_error, "error");
    },
  });

  const km_distance =
    exerciseType === "cardio" ? selectedExercise.distance / 1000 : 0;
  const minutes =
    exerciseType === "cardio"
      ? parseInt(selectedExercise.value / (60 * km_distance))
      : 0;
  const seconds =
    exerciseType === "cardio"
      ? (selectedExercise.value - minutes * km_distance * 60) / km_distance
      : 0;
  const convertedMinutes = minutes < 10 ? "0" + minutes : minutes;

  const convertedSeconds =
    parseInt(seconds) < 10 ? "0" + parseInt(seconds) : parseInt(seconds);
  const pace = convertedMinutes + ":" + convertedSeconds + " /km";

  useEffect(() => {
    if (exerciseType === "cardio") {
      mutate({
        exerciseId: selectedExercise.exercise,
        distance: selectedExercise.distance,
        category: exerciseType,
      });
    } else {
      mutate({
        exerciseId: selectedExercise.exercise,
        category: exerciseType,
      });
    }
  }, [selectedExercise.exercise]);

  const dataKey =
    exerciseType === "weightlifting" && settings.weight === 1
      ? "weight_lb"
      : "value";

  useEffect(() => {
    if (chartData.length > 0) {
      let progress = chartData[chartData.length - 1].value - chartData[0].value;
      setImpPCt(Math.round((progress * 100) / chartData[0].value));
    }
  }, [chartData]);

  const weight_unit = settings.weight === 0 ? "kg" : "lb";
  return (
    <Stack width={"100%"} gap={1}>
      <Stack height={"25vh"} minHeight={"200px"} gap={1}>
        <Box>
          <SecondaryTitle>Progress over time</SecondaryTitle>
        </Box>
        <ResponsiveContainer width={"100%"} height={"100%"}>
          <LineChart
            data={chartData}
            margin={{
              top: 10,
              right: 30,
              left: -30,
            }}
          >
            <YAxis
              tick={{ fill: Theme.palette.text, fontFamily: "DM sans" }}
              fontSize={"0.8rem"}
              fontFamily={"DM sans"}
              tickFormatter={(value) =>
                exerciseType === "cardio" ? parseInt(value / 60) : value
              }
            />
            <XAxis
              tick={{ fill: Theme.palette.text, fontFamily: "DM sans" }}
              fontSize={"0.8rem"}
              fontFamily={"DM sans"}
              dataKey="created_at"
              tickFormatter={(value) => dayjs(value).format("DD/M")}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Line
              type={"monotone"}
              dataKey={dataKey}
              stroke={
                exerciseType === "cardio"
                  ? Theme.palette.up
                  : exerciseType === "gymnastics"
                    ? Theme.palette.blue
                    : Theme.palette.gold
              }
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </Stack>
      <Stack gap={1}>
        <SecondaryTitle>Stats</SecondaryTitle>
        <SpacedRow gap={1}>
          {exerciseType === "cardio" && (
            <StatsBanner
              icon={"⚡"}
              place={pace}
              text={"Avg Pace"}
              color={
                exerciseType === "cardio"
                  ? Theme.palette.up
                  : exerciseType === "gymnastics"
                    ? Theme.palette.blue
                    : Theme.palette.gold
              }
            />
          )}
          <StatsBanner
            icon={"📈"}
            place={impPct + "%"}
            text={"Improvement"}
            color={
              exerciseType === "cardio"
                ? Theme.palette.up
                : exerciseType === "gymnastics"
                  ? Theme.palette.blue
                  : Theme.palette.gold
            }
          />
          <StatsBanner
            icon={"🔥"}
            place={
              <>
                {exerciseType === "cardio"
                  ? chartData[chartData.length - 1]?.converted_score
                  : exerciseType === "weightlifting" && settings.weight === 1
                    ? chartData[chartData.length - 1]?.weight_lb
                    : chartData[chartData.length - 1]?.value}
                &nbsp;
                <span
                  style={{
                    fontSize: "0.6rem",
                    letterSpacing: "1.4px",
                    fontFamily: "DM Sans, sans-serif",
                    fontWeight: 700,
                    color:
                      exerciseType === "cardio"
                        ? Theme.palette.up + "88"
                        : exerciseType === "gymnastics"
                          ? Theme.palette.blue + "88"
                          : Theme.palette.gold + "88",
                  }}
                >
                  {exerciseType === "cardio"
                    ? "min"
                    : exerciseType === "weightlifting"
                      ? weight_unit
                      : null}
                </span>
              </>
            }
            text={"Last Session"}
            color={
              exerciseType === "cardio"
                ? Theme.palette.up
                : exerciseType === "gymnastics"
                  ? Theme.palette.blue
                  : Theme.palette.gold
            }
          />
        </SpacedRow>
      </Stack>
    </Stack>
  );
};

export default ExerciseChart;
